import React from 'react';
import { CreditCard, Loader2 } from 'lucide-react';

const PaymentMethodsCard = ({ 
  paymentMethods = [], 
  loading = false, 
  periodDescription = "Todos os dados" 
}) => {
  if (paymentMethods.length === 0) return null;

  const colors = {
    credit_card: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', bar: 'bg-blue-500', accent: 'border-blue-300' },
    debit_card: { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700', bar: 'bg-cyan-500', accent: 'border-cyan-300' },
    cash: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', bar: 'bg-emerald-500', accent: 'border-emerald-300' },
    pix: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', bar: 'bg-purple-500', accent: 'border-purple-300' },
    voucher: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', bar: 'bg-amber-500', accent: 'border-amber-300' },
    boleto: { bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', bar: 'bg-slate-500', accent: 'border-slate-300' },
    other: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', bar: 'bg-gray-500', accent: 'border-gray-300' }
  };

  const totalGeral = paymentMethods.reduce((sum, pm) => sum + (pm.total_valor || 0), 0);
  const totalVendas = paymentMethods.reduce((sum, pm) => sum + (pm.num_vendas || 0), 0);
  const ticketMedioGeral = totalVendas > 0 ? totalGeral / totalVendas : 0;

  return (
    <div className="p-4 bg-white rounded-xl border border-slate-200 mt-4">
      <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
        <CreditCard className="w-4 h-4 text-[#4A90E2]" />
        Vendas por Forma de Pagamento
        <span className="text-xs text-slate-400 font-normal ml-2">
          ({periodDescription})
        </span>
        {loading && <Loader2 className="w-4 h-4 animate-spin ml-2" />}
      </h4>
      
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {paymentMethods.map((pm, index) => {
          const color = colors[pm.categoria] || colors.other;
          const ticketMedio = pm.ticket_medio || (pm.num_vendas > 0 ? pm.total_valor / pm.num_vendas : 0);
          
          return (
            <div key={index} className={`p-3 rounded-lg ${color.bg} border ${color.border}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{pm.icon}</span>
                <span className={`font-medium text-sm ${color.text}`}>{pm.forma_pagamento}</span>
              </div>
              <p className={`font-bold text-lg ${color.text}`}>
                R$ {pm.total_valor?.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
              </p>
              <p className="text-xs text-slate-500">{pm.num_vendas?.toLocaleString('pt-BR')} vendas</p>
              {/* Ticket Médio destacado */}
              <div className={`mt-2 p-1.5 bg-white/70 rounded border ${color.accent}`}>
                <p className="text-[10px] text-slate-500 uppercase tracking-wide">Ticket Médio</p>
                <p className={`font-bold text-sm ${color.text}`}>
                  R$ {ticketMedio.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
                </p>
              </div>
              {/* Progress bar */}
              <div className="h-2 bg-white/50 rounded-full overflow-hidden mt-2">
                <div 
                  className={`h-full ${color.bar} rounded-full transition-all duration-500`}
                  style={{ width: `${pm.percentual}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">{pm.percentual}%</p>
            </div>
          );
        })}
      </div>
      
      {/* Total with Ticket Médio Geral */}
      <div className="mt-4 pt-3 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-4">
          <div>
            <span className="text-xs text-slate-500">Total Geral</span>
            <p className="font-bold text-lg text-[#4A90E2]">
              R$ {totalGeral.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
            </p>
          </div>
          <div className="border-l border-slate-200 pl-4">
            <span className="text-xs text-slate-500">Total Vendas</span>
            <p className="font-bold text-lg text-slate-700">
              {totalVendas.toLocaleString('pt-BR')}
            </p>
          </div>
        </div>
        <div className="p-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
          <span className="text-xs text-blue-600 font-medium">Ticket Médio Geral</span>
          <p className="font-bold text-xl text-blue-700">
            R$ {ticketMedioGeral.toLocaleString('pt-BR', {minimumFractionDigits: 2})}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodsCard;
